import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementType
} from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon'
import { textVariants } from '../../atoms/text/text'

export interface BreadcrumbItem {
  label: string
  href: string
}

/**
 * Ømni rule: past this many levels (home excluded), older ancestors fold into
 * an ellipsis, keeping the two closest ancestors and the current page visible.
 */
const MAX_UNCOLLAPSED_LEVELS = 3

// !no-underline: same RootLayout workaround as marketing-banner (see its comment).
const LINK_CLASSES =
  'text-surface-body hover:text-surface-title-50 !no-underline transition-colors'

// The hover unfold rides the Accordion's spring: `--breadcrumb-spring` below is
// a verbatim copy of `--accordion-spring`, regenerate both together. It has to
// interpolate max-width between two lengths — `none` and `max-content` don't
// animate outside Chromium (interpolate-size is Chromium-only) — so the hover
// cap is a fixed 640px; a label longer than that stays cut even on hover. The
// transition is declared on the hover state only, so entering animates and
// leaving snaps back: animating the way out would sit still until the cap
// crossed the text.
const LABEL_CLASSES =
  'max-w-60 truncate hover:max-w-[40rem] motion-safe:hover:[transition:max-width_var(--breadcrumb-spring),color_var(--default-transition-duration)_var(--default-transition-timing-function)]'

const breadcrumbVariants = tv(
  {
    slots: {
      root: 'text-surface-body [--breadcrumb-spring:750ms_linear(0,0.0671,0.215,0.3869,0.5505,0.69,0.8,0.8814,0.9381,0.975,0.9972,1.0091,1.0142,1.0151,1.0138,1.0114,1.0088,1.0064,1.0043,1.0028,1.0016,1.0008,1.0003,1,1)]',
      list: 'm-0 flex list-none items-center gap-1 p-0',
      item: 'flex shrink-0 items-center gap-1',
      separator: 'text-surface-300 shrink-0',
      homeLink: [LINK_CLASSES, 'flex'],
      link: [LINK_CLASSES, LABEL_CLASSES],
      current: [LABEL_CLASSES, 'text-bright-blue font-semibold']
    },
    variants: {
      /* Only the tier changes; the icons follow its line-height through the
         Icon atom's default `inherit` size. */
      size: {
        md: { root: textVariants({ variant: 'body-md' }) },
        sm: {
          root: textVariants({ variant: 'caption-lg', weight: 'semibold' })
        }
      }
    },
    defaultVariants: {
      size: 'md'
    }
  },
  /* tv's bundled tailwind-merge doesn't know the Ømni text tokens; merging is
     owned by the cn() on the root instead (same as badge and text). */
  { twMerge: false }
)

export type BreadcrumbSize = NonNullable<
  VariantProps<typeof breadcrumbVariants>['size']
>

export interface BreadcrumbProps
  extends Omit<ComponentPropsWithoutRef<'nav'>, 'children'> {
  /** Complete navigation hierarchy between home and the current page, in order. Every entry is a link. */
  items: BreadcrumbItem[]
  /** The page being viewed. Rendered as text, never as a link. */
  current: string
  /** Where the house points: the overview of the product the page belongs to. */
  homeHref: string
  /** Accessible name of the house link. */
  homeLabel?: string
  size?: BreadcrumbSize
  /**
   * Rendered for every link. Pass the app's router link for client-side
   * navigation; defaults to a plain anchor.
   */
  component?: ElementType<ComponentPropsWithoutRef<'a'>>
}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      current,
      homeHref,
      homeLabel = 'Home',
      size,
      component: Link = 'a',
      className,
      'aria-label': ariaLabel = 'Breadcrumb',
      ...props
    },
    ref
  ) => {
    const styles = breadcrumbVariants({ size })
    const itemClass = styles.item()
    const linkClass = styles.link()
    const separator = (
      <Icon
        name='keyboard_arrow_right'
        aria-hidden='true'
        className={styles.separator()}
      />
    )

    const levels = items.length + 1
    const collapsed = levels > MAX_UNCOLLAPSED_LEVELS
    const trail = collapsed ? items.slice(-2) : items

    return (
      <nav
        ref={ref}
        data-slot='breadcrumb'
        aria-label={ariaLabel}
        className={cn(styles.root(), className)}
        {...props}
      >
        <ol className={styles.list()}>
          <li className={itemClass}>
            <Link
              href={homeHref}
              aria-label={homeLabel}
              className={styles.homeLink()}
            >
              <Icon name='home' aria-hidden='true' />
            </Link>
          </li>
          {collapsed && (
            <li aria-hidden='true' className={itemClass}>
              {separator}
              <span>...</span>
            </li>
          )}
          {trail.map(({ label, href }, index) => (
            <li key={`${href}-${index}`} className={itemClass}>
              {separator}
              <Link href={href} className={linkClass}>
                {label}
              </Link>
            </li>
          ))}
          <li className={itemClass}>
            {separator}
            <span aria-current='page' className={styles.current()}>
              {current}
            </span>
          </li>
        </ol>
      </nav>
    )
  }
)
Breadcrumb.displayName = 'Breadcrumb'
